import { Box, ButtonBase, Icon, styled } from '@mui/material';
import useSettings from 'app/hooks/useSettings';
import React, { Fragment, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Paragraph, Span } from '../Typography';
import MatxVerticalNavExpansionPanel from './MatxVerticalNavExpansionPanel';

/* ─── Google Fonts Import ─────────────────────────────────────── */
const fontImport = document.createElement('style');
fontImport.textContent = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600&family=Syne:wght@600;700&display=swap');`;
document.head.appendChild(fontImport);

/* ─── Design Tokens ───────────────────────────────────────────── */
const tokens = {
	accent: '#6EE7B7', // emerald-300
	accentGlow: 'rgba(110,231,183,0.18)',
	accentSolid: 'rgba(110,231,183,0.12)',
	activeBar: '#6EE7B7',
	surface: 'rgba(255,255,255,0.04)',
	surfaceHover: 'rgba(255,255,255,0.07)',
	textPrimary: 'rgba(255,255,255,0.92)',
	textMuted: 'rgba(255,255,255,0.38)',
	textLabel: 'rgba(255,255,255,0.28)',
	border: 'rgba(255,255,255,0.06)',
	radius: '10px',
	height: '44px',
	transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
};

/* ─── Section Label ───────────────────────────────────────────── */
const ListLabel = styled(Paragraph)(({ mode }) => ({
	fontFamily: "'Syne', sans-serif",
	fontSize: '10px',
	fontWeight: 700,
	letterSpacing: '0.12em',
	textTransform: 'uppercase',
	color: tokens.textLabel,
	marginTop: '10px',
	marginBottom: '6px',
	paddingLeft: '18px',
	display: mode === 'compact' ? 'none' : 'flex',
	alignItems: 'center',
	gap: '8px',
	'&::after': {
		content: '""',
		flex: 1,
		height: '1px',
		background: tokens.border,
		marginRight: '12px',
	},
}));

/* ─── Shared nav-item styles ──────────────────────────────────── */
const sharedItem = {
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	borderRadius: tokens.radius,
	height: tokens.height,
	whiteSpace: 'pre',
	marginBottom: '3px',
	textDecoration: 'none',
	justifyContent: 'space-between',
	transition: tokens.transition,
	cursor: 'pointer',
	'&:hover': {
		background: tokens.surfaceHover,
		'& .nav-icon': { color: tokens.accent },
		'& .nav-label': { color: tokens.textPrimary },
	},
	'&.compactNavItem': {
		justifyContent: 'center !important',
		paddingLeft: 0,
	},
};

const ExternalLink = styled('a')(({ theme }) => ({
	...sharedItem,
	color: 'rgba(255,255,255,0.68)',
}));

const InternalLink = styled(Box)(() => ({
	'& a': {
		...sharedItem,
		color: 'rgba(255,255,255,0.68)',
	},
	/* Active state */
	'& a.navItemActive': {
		background: tokens.accentSolid,
		boxShadow: `inset 0 0 0 1px ${tokens.accentGlow}`,
		'& .nav-icon': { color: tokens.accent },
		'& .nav-label': { color: tokens.textPrimary, fontWeight: 600 },
		/* left accent bar */
		'&::before': {
			content: '""',
			position: 'absolute',
			left: 0,
			top: '8px',
			bottom: '8px',
			width: '3px',
			borderRadius: '0 3px 3px 0',
			background: tokens.activeBar,
			boxShadow: `0 0 8px ${tokens.accent}`,
		},
	},
}));

/* ─── Text label ─────────────────────────────────────────────── */
const StyledText = styled(Span)(({ mode }) => ({
	fontFamily: "'Plus Jakarta Sans', sans-serif",
	fontSize: '0.84rem',
	fontWeight: 500,
	paddingLeft: '0.5rem',
	flex: 1,
	display: mode === 'compact' ? 'none' : 'block',
	transition: tokens.transition,
	className: 'nav-label',
}));

/* ─── Bullet for leaf items without icon ────────────────────── */
const BulletIcon = styled('div')(() => ({
	width: '6px',
	height: '6px',
	marginLeft: '26px',
	marginRight: '14px',
	borderRadius: '50%',
	flexShrink: 0,
	background: 'rgba(255,255,255,0.25)',
	transition: tokens.transition,
}));

/* ─── Badge ──────────────────────────────────────────────────── */
const BadgeValue = styled('div')(() => ({
	fontFamily: "'Plus Jakarta Sans', sans-serif",
	fontSize: '10px',
	fontWeight: 700,
	padding: '2px 8px',
	borderRadius: '20px',
	background: tokens.accentGlow,
	color: tokens.accent,
	border: `1px solid ${tokens.accentGlow}`,
	marginRight: '10px',
	letterSpacing: '0.03em',
}));

/* ─── Icon wrapper ───────────────────────────────────────────── */
const NavIcon = styled(Icon)(() => ({
	fontSize: '19px !important',
	width: '36px',
	paddingLeft: '14px',
	paddingRight: '8px',
	verticalAlign: 'middle',
	color: 'rgba(255,255,255,0.45)',
	transition: tokens.transition,
	flexShrink: 0,
	className: 'nav-icon',
}));

/* ─── Main component ─────────────────────────────────────────── */
const MatxVerticalNav = ({ items }) => {
	const { settings } = useSettings();
	const { mode } = settings.layout1Settings.leftSidebar;

	const renderLevels = (data) =>
		data.map((item, index) => {
			/* Section label */
			if (item.type === 'label')
				return (
					<ListLabel key={index} mode={mode} className='sidenavHoverShow'>
						{item.label}
					</ListLabel>
				);

			/* Expandable group */
			if (item.children) {
				return (
					<MatxVerticalNavExpansionPanel mode={mode} item={item} key={index}>
						{renderLevels(item.children)}
					</MatxVerticalNavExpansionPanel>
				);
			}

			/* External link */
			if (item.type === 'extLink') {
				return (
					<ExternalLink
						key={index}
						href={item.path}
						className={mode === 'compact' ? 'compactNavItem' : ''}
						rel='noopener noreferrer'
						target='_blank'>
						<ButtonBase
							name='child'
							sx={{
								width: '100%',
								height: '100%',
								borderRadius: tokens.radius,
							}}>
							{item.icon ? (
								<NavIcon className='nav-icon'>{item.icon}</NavIcon>
							) : (
								<span
									className='item-icon icon-text'
									style={{ paddingLeft: 16 }}>
									{item.iconText}
								</span>
							)}
							<StyledText mode={mode} className='nav-label sidenavHoverShow'>
								{item.name}
							</StyledText>
							<Box flex={1} />
							{item.badge && (
								<BadgeValue className='sidenavHoverShow'>
									{item.badge.value}
								</BadgeValue>
							)}
						</ButtonBase>
					</ExternalLink>
				);
			}

			/* Internal link */
			return (
				<InternalLink key={index}>
					<NavLink
						to={item.path}
						className={({ isActive }) =>
							[
								isActive ? 'navItemActive' : '',
								mode === 'compact' ? 'compactNavItem' : '',
							]
								.filter(Boolean)
								.join(' ')
						}>
						<ButtonBase
							name='child'
							sx={{
								width: '100%',
								height: '100%',
								borderRadius: tokens.radius,
							}}>
							{item?.icon ? (
								<NavIcon className='nav-icon'>{item.icon}</NavIcon>
							) : (
								<Fragment>
									<BulletIcon
										className='nav-bullet'
										sx={{ display: mode === 'compact' ? 'none' : undefined }}
									/>
									<Box
										className='nav-bullet-text'
										sx={{
											ml: '20px',
											fontSize: '11px',
											color: 'rgba(255,255,255,0.5)',
											display: mode !== 'compact' ? 'none' : undefined,
										}}>
										{item.iconText}
									</Box>
								</Fragment>
							)}
							<StyledText mode={mode} className='nav-label sidenavHoverShow'>
								{item.name}
							</StyledText>
							<Box flex={1} />
							{item.badge && (
								<BadgeValue className='sidenavHoverShow'>
									{item.badge.value}
								</BadgeValue>
							)}
						</ButtonBase>
					</NavLink>
				</InternalLink>
			);
		});

	return (
		<Box
			className='navigation'
			sx={{
				px: '10px',
				py: '8px',
				fontFamily: "'Plus Jakarta Sans', sans-serif",
				/* Scrollbar styling */
				overflowY: 'auto',
				'&::-webkit-scrollbar': { width: '4px' },
				'&::-webkit-scrollbar-track': { background: 'transparent' },
				'&::-webkit-scrollbar-thumb': {
					background: 'rgba(255,255,255,0.1)',
					borderRadius: '4px',
					'&:hover': { background: 'rgba(255,255,255,0.2)' },
				},
			}}>
			{renderLevels(items)}
		</Box>
	);
};

export default React.memo(MatxVerticalNav);
